import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../translations';
import logo from '../assets/logo-optimized.webp';
import { Globe } from 'lucide-react';

const Navbar = ({ minimal = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);

  const dropdownRef = useRef(null);
  const hoverTimeoutRef = useRef(null);

  const { language, toggleLanguage } = useLanguage();
  const location = useLocation();
  const t = translations[language];

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 20) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsServicesOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (minimal) {
    return (
      <nav role="navigation" aria-label="Campaign Navigation" className="fixed top-0 z-50 w-full border-b border-edge-light bg-white py-4">
        <div className="container mx-auto flex items-center justify-between px-4 sm:px-6 lg:px-8">
          <Link to="/" className="group/logo flex items-center logo-hover shrink-0" aria-label="Connecto - Home">
            <img src={logo} alt="كونكتو" width="300" height="106" fetchPriority="high" className="h-8 w-auto sm:h-9" />
          </Link>
          <button
            onClick={toggleLanguage}
            aria-label={language === 'ar' ? 'Switch to English' : 'تغيير للغة العربية'}
            className="inline-flex items-center gap-1.5 rounded-xl border border-edge-medium bg-surface-section px-3 py-1.5 text-xs font-bold text-dark transition-all hover:border-dark hover:bg-white"
          >
            <Globe size={14} aria-hidden="true" />
            <span className="uppercase tracking-wide">{language === 'ar' ? 'EN' : 'عربي'}</span>
          </button>
        </div>
      </nav>
    );
  }

  const navItems = [
    {
      name: t.services,
      path: '/services/',
      children: [
        { name: t.navAccountSetup || (language === 'ar' ? 'افتح حساب' : 'Account Setup'), path: '/service/account-setup/' },
        { name: t.navAccountMgmt || (language === 'ar' ? 'إدارة الحسابات' : 'Account Management'), path: '/service/account-management/' },
        { name: t.navConnectoPlatform || (language === 'ar' ? 'منصة كونكتو' : 'Connecto Platform'), path: '/service/platform/' },
      ],
    },
    { name: t.navIntegrations || (language === 'ar' ? 'المنصات المدعومة' : 'Connected Marketplace'), path: '/supported-marketplaces/' },
    { name: t.pricing, path: '/pricing/' },
    { name: t.about, path: '/about/' },
    { name: t.partners, path: '/partners/' },
  ];

  const handleMouseEnter = () => {
    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    setIsServicesOpen(true);
  };

  const handleMouseLeave = () => {
    hoverTimeoutRef.current = setTimeout(() => {
      setIsServicesOpen(false);
    }, 150);
  };

  return (
    <nav 
      role="navigation"
      aria-label="Main Navigation"
      className={`fixed top-0 w-full z-50 animate-navbar-in transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-md py-3' : 'bg-white py-4'
      }`}
      ref={dropdownRef}
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center">
          <Link 
            to="/" 
            className="group/logo flex items-center logo-hover shrink-0"
            aria-label="Connecto - Home"
          >
            <img
              src={logo}
              alt="كونكتو"
              width="300"
              height="106"
              fetchPriority="high"
              className="h-8 sm:h-9 w-auto transition-transform duration-500 cubic-bezier(0.16,1,0.3,1) group-hover/logo:scale-[1.1] group-hover/logo:brightness-110"
            />
          </Link>

          {/* Center Navigation Links */}
          <div className="hidden xl:flex items-center justify-center gap-4 2xl:gap-7 flex-1 px-4 min-w-0">
            {navItems.map((item) => {
              if (item.children) {
                return (
                  <div
                    key={item.name}
                    className="relative shrink-0"
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                  >
                    <button
                      onClick={() => setIsServicesOpen(!isServicesOpen)}
                      className={`flex items-center gap-1.5 text-nav hover:text-dark font-medium transition-colors duration-200 text-sm whitespace-nowrap ${
                        item.children.some((c) => location.pathname === c.path) ? 'text-dark font-bold' : ''
                      }`}
                    >
                      <span>{item.name}</span>
                      <svg
                        className={`w-4 h-4 transition-transform duration-200 ${
                          isServicesOpen ? 'rotate-180' : ''
                        }`}
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2}
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>

                    {isServicesOpen && (
                      <div
                        className={`absolute top-full ${
                          language === 'ar' ? 'right-0' : 'left-0'
                        } mt-2 bg-white rounded-2xl shadow-xl border border-edge-medium py-2 min-w-[210px] z-50 animate-fade-in`}
                      >
                        {item.children.map((child) => (
                          <Link
                            key={child.path}
                            to={child.path}
                            onClick={() => setIsServicesOpen(false)}
                            className={`block px-4 py-2.5 text-sm transition-colors duration-150 rounded-xl mx-1.5 font-medium ${
                              location.pathname === child.path
                                ? 'text-dark bg-surface-section font-bold'
                                : 'text-heading hover:bg-surface-section hover:text-dark'
                            }`}
                          >
                            {child.name}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                );
              }

              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`nav-link-animated text-nav hover:text-dark font-medium transition-colors duration-200 text-sm whitespace-nowrap shrink-0 ${
                    location.pathname === item.path ? 'text-dark font-bold' : ''
                  }`}
                >
                  {item.name}
                </Link>
              );
            })}
          </div>

          {/* Right Side Action Cluster */}
          <div className="hidden xl:flex items-center gap-2.5 2xl:gap-3 shrink-0">
            {/* Compact Language Toggle Pill */}
            <button
              onClick={toggleLanguage}
              aria-label={language === 'ar' ? 'Switch to English' : 'تغيير للغة العربية'}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-edge-medium bg-surface-section text-dark hover:bg-white hover:border-dark transition-all duration-200 text-xs font-bold font-ibm shadow-2xs group"
            >
              <Globe size={14} className="text-body-muted group-hover:text-dark transition-colors" />
              <span className="uppercase tracking-wide font-extrabold text-[11px]">{language === 'ar' ? 'EN' : 'عربي'}</span>
            </button>
            
            {/* Auth Buttons Cluster */}
            <div className="flex items-center gap-2">
              <a
                href="https://platform.connecto-me.com/login"
                target="_blank"
                rel="noopener noreferrer"
                className="text-dark bg-white px-3.5 py-2 text-sm rounded-xl border border-edge-medium hover:border-dark hover:bg-surface-section transition-all duration-200 font-bold font-almarai whitespace-nowrap flex items-center gap-1.5 shadow-2xs"
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/>
                  <polyline points="10 17 15 12 10 7"/>
                  <line x1="15" y1="12" x2="3" y2="12"/>
                </svg>
                <span>{t.login}</span>
              </a>
              
              <a
                href="https://platform.connecto-me.com/signup"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 rounded-xl font-bold font-almarai text-sm bg-dark text-white border-2 border-dark hover:bg-white hover:text-dark transition-all duration-200 shadow-md whitespace-nowrap px-4 py-2"
                aria-label={t.freeTrial}
              >
                <span>{t.freeTrial}</span>
                <svg width="16" height="16" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"
                  className={language === 'ar' ? 'transform rotate-180' : ''}
                >
                  <path d="M4.16669 10H15.8334M15.8334 10L11.6667 5.83331M15.8334 10L11.6667 14.1667" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </a>
            </div>
          </div>

          <div className="xl:hidden flex items-center gap-3">
            <button
              onClick={toggleLanguage}
              aria-label={language === 'ar' ? 'Switch to English' : 'تغيير للغة العربية'}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-edge-medium bg-surface-section text-dark hover:bg-white hover:border-dark transition-all duration-200 text-xs font-semibold font-almarai"
            >
              <Globe size={14} className="text-body-muted" />
              <span>{language === 'ar' ? 'EN' : 'عربي'}</span>
            </button>

            <button
              onClick={() => setIsOpen(!isOpen)}
              aria-expanded={isOpen}
              aria-label="Toggle Menu"
              className="p-2 rounded-lg hover:bg-surface-hover transition-colors"
            >
              <svg
                className="w-5 h-5 text-nav"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                {isOpen ? (
                  <path d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </div>

        {isOpen && (
          <div className="xl:hidden absolute top-full left-0 right-0 bg-white shadow-lg border-t mt-2 animate-menu-in">
            <div className="container mx-auto px-4 py-4">
              {navItems.map((item) => {
                if (item.children) {
                  return (
                    <div key={item.name} className="border-b border-edge-light">
                      <button
                        onClick={() => setMobileServicesOpen(!mobileServicesOpen)}
                        className="flex items-center justify-between w-full py-3 text-nav font-medium text-left"
                      >
                        <span>{item.name}</span>
                        <svg
                          className={`w-4 h-4 transition-transform duration-200 ${
                            mobileServicesOpen ? 'rotate-180' : ''
                          }`}
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={2}
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                      {mobileServicesOpen && (
                        <div className={`pb-2 ${language === 'ar' ? 'pr-4' : 'pl-4'} space-y-1`}>
                          {item.children.map((child) => (
                            <Link
                              key={child.path}
                              to={child.path}
                              className={`block py-2 text-sm font-medium ${
                                location.pathname === child.path
                                  ? 'text-dark font-bold'
                                  : 'text-body-muted hover:text-dark'
                              }`}
                              onClick={() => setIsOpen(false)}
                            >
                              {child.name}
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                }

                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className="block py-3 text-nav hover:text-dark font-medium border-b border-edge-light last:border-0"
                    onClick={() => setIsOpen(false)}
                  >
                    {item.name}
                  </Link>
                );
              })}
              <div className={`flex items-center ${language === 'ar' ? 'gap-4' : 'gap-6'} mt-4 pt-4 border-t border-edge-light`}>
                <a
                  href="https://platform.connecto-me.com/login"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-dark bg-white px-4 py-2 text-sm rounded-xl border border-edge-medium hover:border-dark hover:bg-surface-section transition-all duration-200 font-bold font-almarai"
                >
                  {t.login}
                </a>
                
                <a
                  href="https://platform.connecto-me.com/signup"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 bg-dark text-white px-5 py-2.5 rounded-xl font-bold font-almarai text-sm border-2 border-dark hover:bg-white hover:text-dark transition-all duration-200 shadow-md"
                  aria-label={t.freeTrial}
                >
                  <span>{t.freeTrial}</span>
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"
                    className={language === 'ar' ? 'transform rotate-180' : ''}
                  >
                    <path d="M4.16669 10H15.8334M15.8334 10L11.6667 5.83331M15.8334 10L11.6667 14.1667" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </a>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;